import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Spinner } from "reactstrap"
import { fetchPlans } from "../../store/subscription/actions"
import { getPlanFeatureCatalog, getPlanIntegrationCatalog } from "../../api/requests/subscription-requests"
import BackButton from "../../components/Common/BackButton";
import { resolvePlanPricing } from "../../utils/planPricing"

const NAVY = "#221C5D"
const ORANGE = "#E56A22"
const GREEN = "#71C30B"

const formatPrice = (price) =>
  new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price) + " ₺"

const PLAN_COLORS = [
  { accent: "#221C5D", light: "#EBF0F7" },
  { accent: "#E56A22", light: "#FEF5EB" },
  { accent: "#71C30B", light: "#F0FAE5" },
  { accent: "#7FCDE3", light: "#EAF7FB" },
  { accent: "#E07A7A", light: "#FDEEEE" },
  { accent: "#9B59B6", light: "#F4ECF7" },
]

const groupByCategory = (rows) =>
  rows.reduce((acc, row) => {
    const group = acc.find((g) => g.category === row.category)
    if (group) group.rows.push(row)
    else acc.push({ category: row.category, rows: [row] })
    return acc
  }, [])

const PlanComparison = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { plans, loading } = useSelector((state) => state.subscription)

  // Özellik ve entegrasyon katalogları (kod, ad, kategori; enum sırasıyla). Sabit liste
  // tutulmaz; enum'a eklenen bir özellik burada kendiliğinden doğru adla ve yerde görünür.
  const [featureCatalog, setFeatureCatalog] = useState([])
  const [integrationCatalog, setIntegrationCatalog] = useState([])

  useEffect(() => {
    document.title = "Plan Karşılaştırma | Tahsilat360"
    dispatch(fetchPlans())
  }, [dispatch])

  useEffect(() => {
    let cancelled = false
    // Katalog alınamazsa kodun kendisi gösterilir; tablo yine çizilir.
    getPlanFeatureCatalog()
      .then((res) => { if (!cancelled) setFeatureCatalog(res?.data || []) })
      .catch(() => {})
    getPlanIntegrationCatalog()
      .then((res) => { if (!cancelled) setIntegrationCatalog(res?.data || []) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  // En az bir pakette olan HER özellik satır olarak gösterilir. Önceden bütün paketlerde
  // ortak olanlar gizleniyordu; paket kartında görünen özellik karşılaştırmada çıkmıyordu.
  // Sıra ve gruplama katalogdan (paket formundaki başlıklar).
  const usedFeatureCodes = new Set(plans.flatMap((plan) => plan.planFeatures || []))
  const knownCodes = new Set(featureCatalog.map((f) => f.code))
  const featureRows = [
    ...featureCatalog.filter((f) => usedFeatureCodes.has(f.code)),
    ...[...usedFeatureCodes].filter((c) => !knownCodes.has(c)).map((c) => ({ code: c, label: c, category: "Diğer" })),
  ]
  const featureGroups = groupByCategory(featureRows)

  // Paketin entegrasyon kümesi boşsa kısıt yok demektir: o pakette hepsi açıktır (kartlardaki kuralla aynı).
  const planHasIntegration = (plan, code) =>
    !plan.planIntegrations || plan.planIntegrations.length === 0 || plan.planIntegrations.includes(code)
  const integrationGroups = groupByCategory(
    integrationCatalog.filter((i) => plans.some((p) => planHasIntegration(p, i.code)))
  )

  const styles = `
    .plan-compare-modern {
      overflow-x: auto;
    }
    .plan-compare-modern .compare-card {
      border-radius: 1.25rem;
      box-shadow: 0 .5rem 2rem rgba(10,49,110,.1);
      overflow: hidden;
      border: 0;
      background: #fff;
    }
    /* Tablo kendi içinde kaydırılır: başlık satırı (paket adları) tepede, özellik adları solda sabit kalır.
       Sayfaya göre yapışkan başlık, yatay kaydırma kutusu yüzünden mümkün değil. */
    .plan-compare-modern .compare-table-wrapper {
      overflow: auto;
      max-height: calc(100vh - 210px);
      -webkit-overflow-scrolling: touch;
    }
    .plan-compare-modern table {
      border-collapse: separate;
      border-spacing: 0;
      min-width: 640px;
      width: 100%;
      background: #fff;
    }
    .plan-compare-modern th,
    .plan-compare-modern td {
      padding: .85rem 1.1rem;
      border-bottom: 1px solid #f0f0f0;
      font-size: .88rem;
      vertical-align: middle;
    }
    .plan-compare-modern th:first-child,
    .plan-compare-modern td:first-child {
      position: sticky;
      left: 0;
      z-index: 2;
      background: #fafbfc;
      font-weight: 600;
      color: #495057;
      min-width: 180px;
      border-right: 2px solid #e9ecef;
    }
    .plan-compare-modern thead th {
      position: sticky;
      top: 0;
      z-index: 4;
      border-bottom: 2px solid #e9ecef;
      background: #fff;
    }
    .plan-compare-modern thead th:first-child {
      background: #fafbfc;
      z-index: 5;
    }
    .plan-compare-modern tbody tr:last-child td {
      border-bottom: none;
    }
    .plan-compare-modern tbody tr:hover td {
      background: #f8f9fa;
      transition: background .15s;
    }
    .plan-compare-modern tbody tr:hover td:first-child {
      background: #f1f3f5;
    }
    .plan-compare-modern .col-plan {
      text-align: center;
      min-width: 170px;
    }
    .plan-compare-modern .col-plan.is-current {
      background: linear-gradient(180deg, #EBF5FB 0%, #fff 100%) !important;
    }
    .plan-compare-modern thead .col-plan.is-current {
      /* Alttaki beyaz katman: yarı saydam degrade, altından kayan satırları göstermesin. */
      background: linear-gradient(180deg, ${NAVY}0D 0%, #fff 100%), #fff !important;
    }
    .plan-compare-modern .plan-header-cell {
      padding: 1.25rem 1rem !important;
    }
    .plan-compare-modern .plan-name-badge {
      font-size: .82rem;
      border-radius: 999px;
      padding: .4rem .85rem;
      color: #fff;
      display: inline-block;
      font-weight: 700;
      letter-spacing: .2px;
    }
    .plan-compare-modern .plan-price-big {
      font-size: 1.5rem;
      font-weight: 800;
      line-height: 1.2;
      margin-top: .35rem;
    }
    .plan-compare-modern .plan-price-old {
      font-size: .8rem;
      text-decoration: line-through;
      color: #adb5bd;
    }
    .plan-compare-modern .plan-period {
      font-size: .72rem;
      color: #6c757d;
      margin-top: .1rem;
    }
    .plan-compare-modern .check-yes {
      color: ${GREEN};
      font-size: 1.3rem;
    }
    .plan-compare-modern .check-no {
      color: #dee2e6;
      font-size: 1.3rem;
    }
    .plan-compare-modern .current-label {
      display: inline-flex;
      align-items: center;
      gap: .25rem;
      font-size: .68rem;
      font-weight: 700;
      background: ${NAVY};
      color: #fff;
      border-radius: 999px;
      padding: .25rem .65rem;
      margin-bottom: .4rem;
      letter-spacing: .3px;
    }
    .plan-compare-modern .action-btn {
      border-radius: 999px;
      font-weight: 600;
      padding: .55rem 1.25rem;
      border: none;
      color: #fff;
      font-size: .85rem;
      transition: all .2s;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0,0,0,.1);
    }
    .plan-compare-modern .action-btn:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); }
    .plan-compare-modern .action-btn:disabled { opacity: .5; cursor: default; filter: none; box-shadow: none; }
    .plan-compare-modern .section-divider td {
      font-size: .72rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: .8px;
      color: #9ca3af;
      background: #fafbfc !important;
      padding: .6rem 1.1rem;
      border-bottom: 2px solid #e9ecef;
    }
    .plan-compare-modern .section-divider td:first-child {
      background: #f3f4f6 !important;
    }
    .plan-compare-modern .limit-value {
      font-weight: 700;
      font-size: .95rem;
      color: #212529;
    }
    .plan-compare-modern .vat-pill {
      display: inline-block;
      border-radius: 999px;
      color: #fff;
      font-size: .72rem;
      padding: .25rem .65rem;
      font-weight: 600;
    }
    .plan-compare-modern .page-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 1rem;
      margin-bottom: 1.25rem;
    }
    @media (max-width: 768px) {
      .plan-compare-modern .page-header { flex-direction: column; align-items: flex-start; }
      .plan-compare-modern .col-plan { min-width: 150px; }
      .plan-compare-modern th:first-child,
      .plan-compare-modern td:first-child { min-width: 140px; font-size: .82rem; }
    }
  `

  return (
    <div className="page-content plan-compare-modern">
      <style>{styles}</style>
      <div className="container-fluid">

        {/* Header */}
        <div className="page-header">
          <div>
            <BackButton to="/subscription/plans" className="mb-2" />
            <h5 className="fw-bold text-dark mb-1">Plan Karşılaştırma</h5>
            <p className="text-muted mb-0" style={{ fontSize: ".88rem" }}>
              Tüm abonelik planlarını yan yana karşılaştırın
            </p>
          </div>
          <div className="text-muted" style={{ fontSize: ".82rem" }}>
            <i className="bx bx-info-circle me-1" />
            {plans.length} plan listeleniyor
          </div>
        </div>

        {loading && plans.length === 0 ? (
          <div className="text-center p-5">
            <Spinner size="lg" color="primary" />
            <h6 className="mt-3 text-muted">Planlar yükleniyor...</h6>
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-5">
            <i className="bx bx-package display-4 d-block mb-2 text-muted" />
            <p className="text-muted">Kullanılabilir plan bulunamadı.</p>
          </div>
        ) : (
          <div className="compare-card">
            <div className="compare-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th style={{ background: "#fafbfc" }} />
                    {plans.map((plan, index) => {
                      const color = PLAN_COLORS[index % PLAN_COLORS.length]
                      const isCurrent = plan.purchaseType === "CURRENT" || (plan.currentPlan && plan.purchaseType !== "RENEWAL")
                      return (
                        <th
                          key={plan.id}
                          className={`col-plan plan-header-cell ${isCurrent ? "is-current" : ""}`}
                          style={{ borderTop: `4px solid ${isCurrent ? NAVY : color.accent + "60"}` }}
                        >
                          {isCurrent && (
                            <div>
                              <span className="current-label">
                                <i className="bx bx-check" /> Mevcut
                              </span>
                            </div>
                          )}
                          <span className="plan-name-badge" style={{ backgroundColor: color.accent }}>
                            {plan.name}
                          </span>
                          {plan.description && (
                            <p className="text-muted mb-0 mt-1" style={{ fontSize: ".76rem" }}>
                              {plan.description}
                            </p>
                          )}
                        </th>
                      )
                    })}
                  </tr>
                </thead>

                <tbody>
                  {/* Fiyatlandirma */}
                  <tr className="section-divider">
                    <td colSpan={plans.length + 1}>
                      <i className="bx bx-wallet me-1" />Fiyatlandırma
                    </td>
                  </tr>

                  <tr>
                    <td>Fiyat</td>
                    {plans.map((plan, index) => {
                      const color = PLAN_COLORS[index % PLAN_COLORS.length]
                      const isCurrent = plan.purchaseType === "CURRENT" || (plan.currentPlan && plan.purchaseType !== "RENEWAL")
                      const { payable, struck } = resolvePlanPricing(plan)
                      return (
                        <td key={plan.id} className={`col-plan ${isCurrent ? "is-current" : ""}`}>
                          {struck != null && <div className="plan-price-old">{formatPrice(struck)}</div>}
                          <div className="plan-price-big" style={{ color: color.accent }}>
                            {formatPrice(payable)}
                          </div>
                          <div className="plan-period">/ {plan.durationLabel || "dönem"}</div>
                        </td>
                      )
                    })}
                  </tr>

                  {/* Limitler */}
                  <tr className="section-divider">
                    <td colSpan={plans.length + 1}>
                      <i className="bx bx-slider-alt me-1" />Kullanım Limitleri
                    </td>
                  </tr>

                  <tr>
                    <td>Maks. Cari Hesap</td>
                    {plans.map((plan) => {
                      const isCurrent = plan.purchaseType === "CURRENT" || (plan.currentPlan && plan.purchaseType !== "RENEWAL")
                      return (
                        <td key={plan.id} className={`col-plan ${isCurrent ? "is-current" : ""}`}>
                          <span className="limit-value">
                            {plan.maxAccounts ? plan.maxAccounts : <span style={{ color: GREEN }}>Sınırsız</span>}
                          </span>
                        </td>
                      )
                    })}
                  </tr>

                  <tr>
                    <td>Maks. Kullanıcı</td>
                    {plans.map((plan) => {
                      const isCurrent = plan.purchaseType === "CURRENT" || (plan.currentPlan && plan.purchaseType !== "RENEWAL")
                      return (
                        <td key={plan.id} className={`col-plan ${isCurrent ? "is-current" : ""}`}>
                          <span className="limit-value">
                            {plan.maxUsers ? plan.maxUsers : <span style={{ color: GREEN }}>Sınırsız</span>}
                          </span>
                        </td>
                      )
                    })}
                  </tr>

                  <tr>
                    <td>Özel Domain</td>
                    {plans.map((plan) => {
                      const isCurrent = plan.purchaseType === "CURRENT" || (plan.currentPlan && plan.purchaseType !== "RENEWAL")
                      return (
                        <td key={plan.id} className={`col-plan ${isCurrent ? "is-current" : ""}`}>
                          {plan.allowCustomDomain
                            ? <i className="bx bx-check-circle check-yes" title="Var" />
                            : <i className="bx bx-x-circle check-no" title="Yok" />}
                        </td>
                      )
                    })}
                  </tr>

                  {/* Ozellikler */}
                  {featureGroups.map((group) => (
                    <React.Fragment key={`f-${group.category}`}>
                      <tr className="section-divider">
                        <td colSpan={plans.length + 1}>
                          <i className="bx bx-star me-1" />{group.category}
                        </td>
                      </tr>
                      {group.rows.map((feature) => (
                        <tr key={feature.code}>
                          <td>{feature.label}</td>
                          {plans.map((plan) => {
                            const isCurrent = plan.purchaseType === "CURRENT" || (plan.currentPlan && plan.purchaseType !== "RENEWAL")
                            const hasFeature = (plan.planFeatures || []).includes(feature.code)
                            return (
                              <td key={plan.id} className={`col-plan ${isCurrent ? "is-current" : ""}`}>
                                {hasFeature
                                  ? <i className="bx bx-check-circle check-yes" />
                                  : <i className="bx bx-x-circle check-no" />}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}

                  {/* Entegrasyonlar */}
                  {integrationGroups.map((group) => (
                    <React.Fragment key={`i-${group.category}`}>
                      <tr className="section-divider">
                        <td colSpan={plans.length + 1}>
                          <i className="bx bx-plug me-1" />{group.category}
                        </td>
                      </tr>
                      {group.rows.map((integration) => (
                        <tr key={integration.code}>
                          <td>{integration.label}</td>
                          {plans.map((plan) => {
                            const isCurrent = plan.purchaseType === "CURRENT" || (plan.currentPlan && plan.purchaseType !== "RENEWAL")
                            return (
                              <td key={plan.id} className={`col-plan ${isCurrent ? "is-current" : ""}`}>
                                {planHasIntegration(plan, integration.code)
                                  ? <i className="bx bx-check-circle check-yes" />
                                  : <i className="bx bx-x-circle check-no" />}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}

                  {/* Aksiyon */}
                  <tr className="section-divider">
                    <td colSpan={plans.length + 1}>
                      <i className="bx bx-rocket me-1" />İşlem
                    </td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 700 }}></td>
                    {plans.map((plan, index) => {
                      const color = PLAN_COLORS[index % PLAN_COLORS.length]
                      const isCurrent = plan.purchaseType === "CURRENT" || (plan.currentPlan && plan.purchaseType !== "RENEWAL")
                      const isRenewal = plan.purchaseType === "RENEWAL"

                      const { label, bgColor, icon, disabled } = (() => {
                        if (isCurrent && !isRenewal)
                          return { label: "Mevcut Plan", bgColor: "#B0B7C3", icon: "bx-check-circle", disabled: true }
                        switch (plan.purchaseType) {
                          case "RENEWAL": {
                            const endDateStr = plan.currentPlanEndDate
                              ? new Date(plan.currentPlanEndDate).toLocaleDateString("tr-TR")
                              : null
                            return {
                              label: endDateStr
                                ? `Yenile (${endDateStr}'den itibaren)`
                                : "Dönem Sonunda Yenile",
                              bgColor: GREEN,
                              icon: "bx-refresh",
                              disabled: false,
                            }
                          }
                          case "UPGRADE":
                            return { label: "Yükselt", bgColor: color.accent, icon: "bx-up-arrow-alt", disabled: false }
                          case "DOWNGRADE":
                            return { label: "Plan Düşür", bgColor: color.accent, icon: "bx-down-arrow-alt", disabled: false }
                          default:
                            return { label: "Satın Al", bgColor: color.accent, icon: "bx-cart", disabled: false }
                        }
                      })()

                      const handleClick = () => {
                        if (disabled) return
                        if (plan.purchaseType === "UPGRADE") {
                          navigate(`/subscription/upgrade/${plan.id}`)
                        } else if (plan.purchaseType === "DOWNGRADE") {
                          navigate("/subscription/plans")
                        } else {
                          navigate(`/subscription/checkout/${plan.id}`)
                        }
                      }

                      return (
                        <td key={plan.id} className={`col-plan ${isCurrent && !isRenewal ? "is-current" : ""}`}>
                          <button
                            className="action-btn"
                            style={{ backgroundColor: bgColor }}
                            disabled={disabled}
                            onClick={handleClick}
                          >
                            <i className={`bx ${icon} me-1`} />
                            {label}
                          </button>
                        </td>
                      )
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlanComparison
